/**
 * Sessions Service
 * API calls for Epic 2: Multi-Session Tracking
 * 
 * Endpoints:
 * - POST /api/sessions - Create new session
 * - GET /api/sessions - List sessions
 * - GET /api/sessions/summary - Daily summary
 * - GET /api/sessions/:id - Get session by ID
 * - PATCH /api/sessions/:id - Update session
 * - DELETE /api/sessions/:id - Delete session
 */

import axiosInstance from '@/lib/axios';
import type {
  CreateSessionRequest,
  CreateSessionResponse,
  UpdateSessionRequest,
  SessionResponse,
  SessionListParams,
  SessionListResponse,
  SummaryParams,
  DailySummaryResponse,
} from '@/types/sessions';

/**
 * Create a new reading session
 * 
 * @param data - Session creation data (end_page required, start_page & goal_id optional)
 * @returns Created session with start_page_resolved
 * 
 * @example
 * // Simple case: Just end_page (start_page defaults from last known page)
 * const session = await sessionsService.create({ end_page: 50 });
 * 
 * @example
 * // With custom start_page (for non-continuous reading)
 * const session = await sessionsService.create({ 
 *   start_page: 100, 
 *   end_page: 120 
 * });
 */
export async function createSession(
  data: CreateSessionRequest
): Promise<CreateSessionResponse> {
  const response = await axiosInstance.post<CreateSessionResponse>(
    '/api/sessions',
    data
  );
  return response.data;
}

/**
 * List reading sessions
 * 
 * @param params - Optional filters (goal_id, date range, limit)
 * @returns Array of sessions
 * 
 * @example
 * // Get all sessions
 * const sessions = await sessionsService.list();
 * 
 * @example
 * // Get sessions for specific goal
 * const sessions = await sessionsService.list({ goal_id: 'uuid-...' });
 * 
 * @example
 * // Get sessions in date range
 * const sessions = await sessionsService.list({
 *   from_date: '2026-01-01',
 *   to_date: '2026-01-31',
 *   limit: 50
 * });
 */
export async function listSessions(
  params?: SessionListParams
): Promise<SessionListResponse> {
  const response = await axiosInstance.get<SessionListResponse>(
    '/api/sessions',
    { params }
  );
  return response.data;
}

/**
 * Get daily summary of pages read
 * 
 * @param params - Date range and timezone
 * @returns Daily summary with pages_read per day
 * 
 * @example
 * const summary = await sessionsService.getSummary({
 *   from_date: '2026-01-01',
 *   to_date: '2026-01-31',
 *   timezone: 'Asia/Bangkok'
 * });
 */
export async function getDailySummary(
  params: SummaryParams
): Promise<DailySummaryResponse> {
  const response = await axiosInstance.get<DailySummaryResponse>(
    '/api/sessions/summary',
    { params }
  );
  return response.data;
}

/**
 * Get a single session by ID
 * 
 * @param id - Session UUID
 * @returns Session data
 */
export async function getSession(id: string): Promise<SessionResponse> {
  const response = await axiosInstance.get<SessionResponse>(
    `/api/sessions/${id}`
  );
  return response.data;
}

/**
 * Update a session
 * 
 * @param id - Session UUID
 * @param data - Update data (currently only end_page)
 * @returns Updated session
 * 
 * @example
 * const updated = await sessionsService.updateSession('uuid-...', { 
 *   end_page: 75 
 * });
 */
export async function updateSession(
  id: string,
  data: UpdateSessionRequest
): Promise<SessionResponse> {
  const response = await axiosInstance.patch<SessionResponse>(
    `/api/sessions/${id}`,
    data
  );
  return response.data;
}

/**
 * Delete a session
 * 
 * @param id - Session UUID
 * @returns void (204 No Content on success)
 * 
 * @example
 * await sessionsService.deleteSession('uuid-...');
 */
export async function deleteSession(id: string): Promise<void> {
  await axiosInstance.delete(`/api/sessions/${id}`);
}

/**
 * Sessions Service - All API methods in one object
 */
export const sessionsService = {
  create: createSession,
  list: listSessions,
  getSummary: getDailySummary,
  get: getSession,
  update: updateSession,
  delete: deleteSession,
};

export default sessionsService;
